// Repair kanban board — /repair
// Columns = 11 statuses, no drag-drop, status change via card dropdown.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";
import { repairApi, STATUS_LABELS, RepairTicket } from "./repair-api";
import { TicketCard } from "./components/ticket-card";

const COLUMN_ORDER = [
  "received", "diagnosing", "quoted", "approved", "repairing",
  "waiting_parts", "sent_to_vendor", "received_back", "done", "delivered", "cancelled",
];

export function RepairKanbanPage() {
  const [filter, setFilter] = useState({ q: "", from: "", to: "" });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["repair-kanban"],
    queryFn: repairApi.kanban,
    refetchInterval: 30_000,
  });

  // Apply client-side text search.
  function filtered(tickets: RepairTicket[]): RepairTicket[] {
    const q = filter.q.toLowerCase();
    let out = tickets;
    if (q) {
      out = out.filter(
        (t) =>
          t.code.toLowerCase().includes(q) ||
          t.deviceDesc.toLowerCase().includes(q) ||
          (t.customerName ?? "").toLowerCase().includes(q),
      );
    }
    if (filter.from) {
      const from = new Date(filter.from).getTime();
      out = out.filter((t) => new Date(t.createdAt).getTime() >= from);
    }
    if (filter.to) {
      const to = new Date(filter.to).getTime() + 86_400_000;
      out = out.filter((t) => new Date(t.createdAt).getTime() <= to);
    }
    return out;
  }

  const groups: Record<string, RepairTicket[]> = data ?? {};

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Sửa chữa</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-md border p-2 text-muted-foreground hover:bg-accent disabled:opacity-40"
            title="Làm mới"
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
          </button>
          <Link
            to="/repair/new"
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} />
            Tạo phiếu sửa
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 rounded-xl border bg-card p-3">
        <input
          type="text"
          placeholder="Tìm mã phiếu, thiết bị, khách hàng..."
          className="rounded-md border bg-background px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-primary"
          value={filter.q}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
        />
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span>Từ</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none"
            value={filter.from}
            onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))}
          />
          <span>đến</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none"
            value={filter.to}
            onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))}
          />
        </div>
        {(filter.q || filter.from || filter.to) && (
          <button
            className="text-xs text-muted-foreground underline"
            onClick={() => setFilter({ q: "", from: "", to: "" })}
          >
            Xoá bộ lọc
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-muted-foreground">Đang tải...</div>
      ) : (
        /* Horizontal-scroll kanban */
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: `${COLUMN_ORDER.length * 220}px` }}>
            {COLUMN_ORDER.map((status) => {
              const tickets = filtered(groups[status] ?? []);
              return (
                <div key={status} className="flex flex-col rounded-xl border bg-muted/30 w-52 shrink-0">
                  {/* Column header */}
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="text-xs font-semibold text-foreground">
                      {STATUS_LABELS[status] ?? status}
                    </span>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {tickets.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex flex-col gap-2 overflow-y-auto p-2" style={{ maxHeight: "70vh" }}>
                    {tickets.length === 0 ? (
                      <p className="py-4 text-center text-[11px] text-muted-foreground">Trống</p>
                    ) : (
                      tickets.map((t) => <TicketCard key={t.id} ticket={t} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
